"use client";

import { AtSign, CalendarClock, Mail } from "lucide-react";
import { AnimatePresence, motion } from "motion/react";
import {
  type KeyboardEvent,
  type ReactNode,
  useRef,
  useState,
  useSyncExternalStore,
} from "react";

import { cn } from "@/lib/utils";

// =============================================================================

export const CONTACT_TABS = ["email", "call", "networks"] as const;
export type ContactTab = (typeof CONTACT_TABS)[number];

const ICONS: Record<ContactTab, ReactNode> = {
  email: <Mail />,
  call: <CalendarClock />,
  networks: <AtSign />,
};

const EASE = [0.16, 1, 0.3, 1] as const;

function tabOf(value: string | null): ContactTab {
  return CONTACT_TABS.find((tab) => tab === value) ?? "email";
}

function subscribeToHistory(onChange: () => void) {
  window.addEventListener("popstate", onChange);
  return () => window.removeEventListener("popstate", onChange);
}

const readUrlTab = () =>
  tabOf(new URLSearchParams(window.location.search).get("tab"));

/**
 * "Send an email | Book a call | Networks": the pill slides to the chosen
 * tab and the panels cross-fade. The tab lives in the URL (?tab=call), so a
 * link can open the calendar directly. It is read without useSearchParams:
 * the server renders the whole tab bar (no Suspense fallback to replace,
 * nothing moves on load) and a client navigation opens the right tab at once.
 */
export function ContactTabs({
  labels,
  panels,
}: {
  labels: Record<ContactTab, string> & { label: string };
  panels: Record<ContactTab, ReactNode>;
}) {
  const urlTab = useSyncExternalStore(
    subscribeToHistory,
    readUrlTab,
    () => "email" as const
  );
  const [picked, setPicked] = useState<ContactTab | null>(null);
  const active = picked ?? urlTab;
  const tabRefs = useRef(new Map<ContactTab, HTMLButtonElement>());

  const select = (tab: ContactTab) => {
    setPicked(tab);
    const url = new URL(window.location.href);
    if (tab === "email") url.searchParams.delete("tab");
    else url.searchParams.set("tab", tab);
    window.history.replaceState(null, "", url);
  };

  const onKeyDown = (event: KeyboardEvent, index: number) => {
    const step =
      event.key === "ArrowRight" ? 1 : event.key === "ArrowLeft" ? -1 : 0;
    if (!step) return;
    event.preventDefault();
    const next =
      CONTACT_TABS[(index + step + CONTACT_TABS.length) % CONTACT_TABS.length];
    select(next);
    tabRefs.current.get(next)?.focus();
  };

  return (
    <div>
      <div
        role="tablist"
        aria-label={labels.label}
        className="relative inline-flex max-w-full gap-1 overflow-x-auto rounded-full border border-foreground/10 bg-surface-1 p-1"
        data-lenis-prevent
      >
        {CONTACT_TABS.map((tab, index) => {
          const isActive = tab === active;
          return (
            <button
              key={tab}
              ref={(element) => {
                if (element) tabRefs.current.set(tab, element);
              }}
              type="button"
              role="tab"
              id={`contact-tab-${tab}`}
              aria-selected={isActive}
              aria-controls={`contact-panel-${tab}`}
              tabIndex={isActive ? 0 : -1}
              onClick={() => select(tab)}
              onKeyDown={(event) => onKeyDown(event, index)}
              className={cn(
                "relative isolate inline-flex shrink-0 items-center gap-2.5 rounded-full px-5 py-3 text-[0.95rem] font-medium whitespace-nowrap transition-colors duration-300 focus-visible:ring-2 focus-visible:ring-foreground/40 focus-visible:outline-none sm:px-6 [&_svg]:size-4",
                isActive
                  ? "text-background"
                  : "text-muted-foreground hover:text-foreground"
              )}
            >
              {isActive && (
                <motion.span
                  layoutId="contact-tab-pill"
                  className="absolute inset-0 -z-10 rounded-full bg-foreground"
                  transition={{ duration: 0.5, ease: EASE }}
                />
              )}
              {ICONS[tab]}
              {labels[tab]}
            </button>
          );
        })}
      </div>

      <div className="mt-[clamp(2.5rem,6vw,5rem)]">
        <AnimatePresence mode="wait" initial={false}>
          <motion.div
            key={active}
            role="tabpanel"
            id={`contact-panel-${active}`}
            aria-labelledby={`contact-tab-${active}`}
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.45, ease: EASE }}
          >
            {panels[active]}
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
}

// =============================================================================
